import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [tasks, setTasks] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showTasksRef, setShowTasksRef] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  
  // Форма трекинга времени
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState('');
  const [comment, setComment] = useState('');
  
  // Фильтры отчета
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');

  // Авторизация
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (token) {
      loadTasks();
      loadRecentEntries();
      setDefaultReportDates();
    }
  }, [token]);

  useEffect(() => {
    if (showTasksRef || showTimeForm) loadTasks();
  }, [showTasksRef, showTimeForm]);

  const formatDateLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDateTime = (str) => {
    if (!str) return '—';
    const d = new Date(str);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${m}.${y} ${h}:${min}`;
  };

  const setDefaultReportDates = () => {
    const today = new Date();
    const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const firstDay = new Date(prevYear, prevMonth, 1);
    const lastDay = new Date(prevYear, prevMonth + 1, 0);
    setReportStartDate(formatDateLocal(firstDay));
    setReportEndDate(formatDateLocal(lastDay));
  };

  const authHeaders = () => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  const handleUnauthorized = () => {
    setToken('');
    setUsername('');
    localStorage.removeItem('token');
    localStorage.removeItem('username');
  };

  const apiFetch = async (url, options = {}) => {
    const mergedOptions = {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
      },
    };

    const response = await fetch(url, mergedOptions);
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized');
    }
    return response;
  };

  const loadRecentEntries = async () => {
    if (!token) return;
    try {
      const response = await apiFetch(`${API_URL}/time-entries?limit=20`);
      const data = await response.json();
      setRecentEntries(data);
    } catch (error) {
      console.error('Error loading recent entries:', error);
    }
  };

  const loadTasks = async () => {
    if (!token) return;
    try {
      const response = await apiFetch(`${API_URL}/tasks`);
      const data = await response.json();
      setTasks(data);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) return;

    try {
      const response = await apiFetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newTaskName }),
      });

      if (response.ok) {
        await loadTasks();
        setNewTaskName('');
        setShowTaskForm(false);
      }
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const addTime = (amount) => {
    const current = parseFloat(hours) || 0;
    setHours((current + amount).toString());
  };

  const updateTask = async (e) => {
    e.preventDefault();
    if (!editingTaskName.trim()) return;

    try {
      const response = await apiFetch(`${API_URL}/tasks/${editingTaskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: editingTaskName }),
      });

      if (response.ok) {
        await loadTasks();
        setEditingTaskId(null);
        setEditingTaskName('');
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const createTimeEntry = async (e) => {
    e.preventDefault();
    if (!selectedTaskId || !selectedDate || !hours) return;

    try {
      const response = await apiFetch(`${API_URL}/time-entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: parseInt(selectedTaskId),
          date: selectedDate,
          hours: parseFloat(hours),
          comment: comment,
        }),
      });

      if (response.ok) {
        setSelectedTaskId('');
        setSelectedDate(new Date().toISOString().split('T')[0]);
        setHours('');
        setComment('');
        setShowTimeForm(false);
        loadRecentEntries();
      }
    } catch (error) {
      console.error('Error creating time entry:', error);
    }
  };

  const loadReport = async () => {
    try {
      const response = await apiFetch(
        `${API_URL}/report?start_date=${reportStartDate}&end_date=${reportEndDate}`
      );
      const data = await response.json();
      setReportData(data);
      setShowReport(true);
    } catch (error) {
      console.error('Error loading report:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setLoginError(data.error || 'Ошибка авторизации');
        return;
      }
      setToken(data.token);
      setUsername(data.username);
      localStorage.setItem('token', data.token);
      localStorage.setItem('username', data.username);
      setLoginPassword('');
      setLoginUsername('');
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Ошибка сети');
    }
  };

  const handleLogout = () => {
    handleUnauthorized();
  };

  if (!token) {
    return (
      <div className="app app-login">
        <header className="app-header">
          <h1>Time Tracker</h1>
        </header>
        <div className="login-card">
          <h2>Вход</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Логин:</label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                className="input"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Пароль:</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="input"
              />
            </div>
            {loginError && <p className="error-text">{loginError}</p>}
            <div className="modal-actions">
              <button type="submit" className="btn btn-primary">
                Войти
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>Time Tracker</h1>
        {username && (
          <div className="user-info">
            <span className="user-name">{username}</span>
            <button onClick={handleLogout} className="btn btn-small btn-secondary">
              Выйти
            </button>
          </div>
        )}
      </header>

      <div className="main-actions">
        <button onClick={() => setShowTaskForm(true)} className="btn btn-primary">
          Создать задачу
        </button>
        <button onClick={() => setShowTimeForm(true)} className="btn btn-primary">
          Track Time
        </button>
        <button onClick={() => setShowTasksRef(true)} className="btn btn-secondary">
          Справочник задач
        </button>
        <button onClick={loadReport} className="btn btn-secondary">
          Отчет
        </button>
      </div>

      <section className="recent-entries">
        <h2>Последние записи</h2>
        <table className="entries-table">
          <thead>
            <tr>
              <th>Дата внесения</th>
              <th>Дата</th>
              <th>Задача</th>
              <th>Часы</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {recentEntries.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-message">Нет записей</td>
              </tr>
            ) : (
              recentEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>{entry.date}</td>
                  <td>{entry.task_name}</td>
                  <td>{entry.hours}</td>
                  <td>{entry.comment || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {showTaskForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Создать задачу</h2>
            <form onSubmit={createTask}>
              <input
                type="text"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                placeholder="Название задачи"
                className="input"
                autoFocus
              />
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  Создать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTaskForm(false);
                    setNewTaskName('');
                  }}
                  className="btn btn-secondary"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTimeForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Track Time</h2>
            <form onSubmit={createTimeEntry}>
              <div className="form-group">
                <label>Задача:</label>
                <select
                  value={selectedTaskId}
                  onChange={(e) => setSelectedTaskId(e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Выберите задачу</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Дата:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Комментарий:</label>
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Комментарий (необязательно)"
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>Время (часы):</label>
                <div className="time-input-group">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="0"
                    className="input time-input"
                    required
                  />
                  <div className="time-buttons">
                    <button
                      type="button"
                      onClick={() => addTime(0.5)}
                      className="btn btn-small"
                    >
                      +0.5ч
                    </button>
                    <button
                      type="button"
                      onClick={() => addTime(1)}
                      className="btn btn-small"
                    >
                      +1ч
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  Сохранить
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTimeForm(false);
                    setSelectedTaskId('');
                    setSelectedDate(new Date().toISOString().split('T')[0]);
                    setHours('');
                    setComment('');
                  }}
                  className="btn btn-secondary"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTasksRef && (
        <div className="modal">
          <div className="modal-content">
            <h2>Справочник задач</h2>
            <ul className="tasks-list">
              {tasks.map((task) => (
                <li key={task.id} className="task-item">
                  {editingTaskId === task.id ? (
                    <form onSubmit={updateTask} className="task-edit-form">
                      <input
                        type="text"
                        value={editingTaskName}
                        onChange={(e) => setEditingTaskName(e.target.value)}
                        className="input input-inline"
                        autoFocus
                      />
                      <button type="submit" className="btn btn-small btn-primary">
                        Сохранить
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingTaskId(null);
                          setEditingTaskName('');
                        }}
                        className="btn btn-small btn-secondary"
                      >
                        Отмена
                      </button>
                    </form>
                  ) : (
                    <>
                      <span className="task-name">{task.name}</span>
                      <button
                        onClick={() => {
                          setEditingTaskId(task.id);
                          setEditingTaskName(task.name);
                        }}
                        className="btn btn-small"
                      >
                        Переименовать
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {tasks.length === 0 && (
              <p className="empty-message">Нет задач. Создайте первую задачу.</p>
            )}
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowTasksRef(false);
                  setEditingTaskId(null);
                  setEditingTaskName('');
                }}
                className="btn btn-secondary"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div className="modal">
          <div className="modal-content modal-large">
            <h2>Отчет</h2>
            <div className="report-filters">
              <div className="form-group">
                <label>С:</label>
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>По:</label>
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="input"
                />
              </div>
              <button onClick={loadReport} className="btn btn-primary">
                Обновить
              </button>
            </div>

            {reportData && (
              <div className="report-content">
                <div className="report-summary">
                  <h3>Итого по задачам:</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Задача</th>
                        <th>Часов</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.map((item) => (
                        <tr key={item.task_id}>
                          <td>{item.task_name}</td>
                          <td>{item.total_hours.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="report-total-row">
                        <td>Итого</td>
                        <td>{reportData.total_hours.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="report-details">
                  <h3>Детали по датам:</h3>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Задача</th>
                        <th>Часов</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.details.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.date}</td>
                          <td>{item.task_name}</td>
                          <td>{item.total_hours.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="report-total-row">
                        <td colSpan="2">Итого</td>
                        <td>{reportData.total_hours.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => setShowReport(false)}
                className="btn btn-secondary"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
